// src/store.js
import UsuarioDataService from "@/services/acessos/UsuarioDataService";
import { getBrowserFcmToken } from "@/services/push/firebase"; // << NOMEADO!
import { authenticateWithPasskey } from "@/utils/passkey";
import { normalizeFontSizePreference } from "@/utils/fontSizePreference";
import { withTwoFactorDevice } from "@/utils/twoFactorDevice";
import { postLoginWithLegacyFallback } from "@/utils/loginCompatibility";
import { ehPushDaPropriaTroca } from "@/utils/trocaEmpresaPush";
import Axios from "axios";
import Vue from "vue";
import Vuex from "vuex";

Vue.use(Vuex);

async function fetchPublicIp(url) {
  if (typeof fetch !== "function" || typeof AbortController !== "function") {
    return null;
  }
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 2000);
  try {
    const response = await fetch(url, {
      signal: controller.signal,
    });
    if (!response.ok) return null;
    const data = await response.json();
    return typeof data.ip === "string" ? data.ip : null;
  } catch (_) {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

async function getPublicIpsForLogin() {
  const [ipv4, ipv6] = await Promise.all([
    fetchPublicIp("https://api.ipify.org?format=json"),
    fetchPublicIp("https://api6.ipify.org?format=json"),
  ]);
  return { ipOrigem: ipv6 || ipv4, ipOrigemIpv4: ipv4 };
}

function postLogin(payload) {
  return postLoginWithLegacyFallback(
    (data) => Axios({ url: "/login", data, method: "POST" }),
    payload
  );
}

function parseJSON(str, fallback) {
  try {
    return JSON.parse(str);
  } catch {
    return fallback;
  }
}

function parseMaybeJson(value) {
  if (value === null || typeof value === "undefined") return null;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return JSON.parse(trimmed);
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[FCM] parseMaybeJson falhou", err, value);
    }
    return null;
  }
}

function normalizeIdentity(value) {
  if (value === null || typeof value === "undefined") return "";
  return String(value);
}

function normalizeMonetaryLimit(value) {
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0 ? value : 0;
  }
  if (value === null || typeof value === "undefined" || value === "") {
    return 0;
  }

  let normalized = String(value)
    .trim()
    .replace(/[^\d,.-]/g, "");
  if (normalized.includes(",")) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function getTenantRoteiroLimits(inquilino = {}) {
  return {
    URBANO: normalizeMonetaryLimit(inquilino?.roteiroLimiteMonetarioUrbano),
    INTERURBANO: normalizeMonetaryLimit(
      inquilino?.roteiroLimiteMonetarioInterurbano
    ),
  };
}

function withTenantRoteiroLimits(inquilino = {}) {
  const limites = getTenantRoteiroLimits(inquilino);
  return {
    ...inquilino,
    roteiroLimiteMonetarioUrbano: limites.URBANO,
    roteiroLimiteMonetarioInterurbano: limites.INTERURBANO,
    limiteSecuritario: limites.URBANO,
  };
}

function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

const defaultUser = {
  id: 0,
  email: "",
  statusComum: "",
  nome: "",
  imagem: "",
  imagem64: "",
  permissoes: [],
  pessoa: { sexo: "MASCULINO", nome: "" },
  alteracaoSenhaObrigatoria: false,
  trocaSenhaPrimeiroAcessoObrigatoria: false,
  tamanhoFonte: "MAIS_ESPACO",
  permitirNotificacoes: true,
};

const dashboardCacheStoragePrefix = "cajupe:last-menu-path-by-root";
const initialInquilino = withTenantRoteiroLimits(
  parseJSON(localStorage.getItem("inquilino"), {})
);
const initialLimitesSecuritariosRoteiro =
  getTenantRoteiroLimits(initialInquilino);

function clearDashboardSessionCache() {
  if (typeof window === "undefined" || !window.sessionStorage) return;

  for (let index = window.sessionStorage.length - 1; index >= 0; index--) {
    const key = window.sessionStorage.key(index);
    if (
      key === dashboardCacheStoragePrefix ||
      key?.startsWith(`${dashboardCacheStoragePrefix}:`)
    ) {
      window.sessionStorage.removeItem(key);
    }
  }
}

function getTenantIdentity(state) {
  const tenantId = state?.inquilino?.id || state?.user?.inquilino?.id || "";
  return normalizeIdentity(tenantId);
}

export default new Vuex.Store({
  state: {
    pathDataService: "@/services/locais/PaisDataService",
    barColor: "rgba(0, 0, 0, .8), rgba(0, 0, 0, .8)",
    token: localStorage.getItem("user-token") || "",
    status: "",

    inquilino: initialInquilino,
    limiteSecuritario: initialLimitesSecuritariosRoteiro.URBANO,
    limitesSecuritariosRoteiro: initialLimitesSecuritariosRoteiro,
    permissoes: parseJSON(localStorage.getItem("permissoes"), []),

    skipTutorialPartModal: false,
    pendingTutorial: [],
    user: parseJSON(localStorage.getItem("user"), defaultUser),

    loading: {
      globalCount: 0,
      userRequest: false,
    },
    dashboardCacheVersion: 0,
  },

  mutations: {
    RESET_DASHBOARD_CACHE(state) {
      state.dashboardCacheVersion += 1;
      clearDashboardSessionCache();
    },

    REMOVE_TOKEN(state) {
      state.dashboardCacheVersion += 1;
      clearDashboardSessionCache();

      state.user = { ...defaultUser };
      state.permissoes = [];
      state.inquilino = {};
      state.limiteSecuritario = 0;
      state.limitesSecuritariosRoteiro = { URBANO: 0, INTERURBANO: 0 };
      state.token = "";
      state.status = "";

      localStorage.removeItem("user-token");
      localStorage.removeItem("permissoes");
      localStorage.removeItem("user");
      localStorage.removeItem("inquilino");
      localStorage.removeItem("horaLogin");
      localStorage.removeItem("fcm_web_token");

      delete Axios.defaults.headers.common["Authorization"];
    },

    SET_TOKEN(state, payload) {
      state.token = payload;
      localStorage.setItem("user-token", payload);
      Axios.defaults.headers.common["Authorization"] = payload;
    },

    AUTH_REQUEST: (state) => {
      state.status = "loading";
    },
    AUTH_SUCCESS: (state, token) => {
      state.status = "success";
      state.token = token;
    },
    AUTH_ERROR: (state) => {
      state.status = "error";
    },
    AUTH_TWO_FACTOR_REQUIRED: (state) => {
      state.status = "two_factor_required";
    },

    SET_USER: (state, response) => {
      const user = {
        ...defaultUser,
        ...(response?.data || {}),
        alteracaoSenhaObrigatoria:
          response?.data?.alteracaoSenhaObrigatoria === true,
        trocaSenhaPrimeiroAcessoObrigatoria:
          response?.data?.trocaSenhaPrimeiroAcessoObrigatoria === true,
        tamanhoFonte: normalizeFontSizePreference(response?.data?.tamanhoFonte),
        permitirNotificacoes: response?.data?.permitirNotificacoes !== false,
      };
      const inquilino = withTenantRoteiroLimits(user?.inquilino || {});
      const limites = getTenantRoteiroLimits(inquilino);
      user.inquilino = inquilino;
      state.user = user;
      state.inquilino = inquilino;
      state.limiteSecuritario = limites.URBANO;
      state.limitesSecuritariosRoteiro = limites;

      localStorage.setItem("user", JSON.stringify(state.user));
      localStorage.setItem("inquilino", JSON.stringify(state.inquilino));

      const permissoesStr =
        (user?.permissoes || []).map((p) => p?.descricao).filter(Boolean) || [];

      state.permissoes = permissoesStr;
      localStorage.setItem("permissoes", JSON.stringify(permissoesStr));
      localStorage.setItem("horaLogin", new Date().toISOString());
    },

    SET_PERMISSOES: (state, response) => {
      const permissoes = [];
      (response?.data || []).forEach((value) => {
        permissoes.push(value?.descricao);
      });
      localStorage.setItem("permissoes", JSON.stringify(permissoes));
      localStorage.setItem("horaLogin", new Date().toISOString());
      state.permissoes = permissoes;
    },

    // Atualiza só o armazenamento local, sem nova consulta (ex.: ao salvar a configuração do eSocial).
    SET_ESOCIAL_ENVIO_ATIVO: (state, ativo) => {
      const esocialEnvioAtivo = ativo === true;
      state.inquilino = { ...state.inquilino, esocialEnvioAtivo };
      state.user = {
        ...state.user,
        inquilino: { ...(state.user?.inquilino || {}), esocialEnvioAtivo },
      };
      localStorage.setItem("inquilino", JSON.stringify(state.inquilino));
      localStorage.setItem("user", JSON.stringify(state.user));
    },

    SET_FONT_SIZE_PREFERENCE: (state, tamanhoFonte) => {
      const preference = normalizeFontSizePreference(tamanhoFonte);
      state.user = { ...state.user, tamanhoFonte: preference };
      localStorage.setItem("user", JSON.stringify(state.user));
    },

    SET_NOTIFICATIONS_PREFERENCE: (state, permitirNotificacoes) => {
      state.user = {
        ...state.user,
        permitirNotificacoes: permitirNotificacoes !== false,
      };
      localStorage.setItem("user", JSON.stringify(state.user));
    },

    SET_SKIP_OPTION_MODAL: (state, payload) => {
      state.skipTutorialPartModal = payload;
    },
    SET_PENDING_TUTORIAL: (state, payload) => {
      state.pendingTutorial = payload;
    },

    HYDRATE_FROM_LOCAL(state) {
      const token = localStorage.getItem("user-token") || "";
      const user = parseJSON(localStorage.getItem("user"), defaultUser);
      const inquilino = withTenantRoteiroLimits(
        parseJSON(localStorage.getItem("inquilino"), {})
      );
      const limites = getTenantRoteiroLimits(inquilino);
      const permissoes = parseJSON(localStorage.getItem("permissoes"), []);

      state.token = token;
      state.user = {
        ...(user || defaultUser),
        inquilino,
        tamanhoFonte: normalizeFontSizePreference(user?.tamanhoFonte),
        permitirNotificacoes: user?.permitirNotificacoes !== false,
      };
      state.inquilino = inquilino || {};
      state.limiteSecuritario = limites.URBANO;
      state.limitesSecuritariosRoteiro = limites;
      state.permissoes = Array.isArray(permissoes) ? permissoes : [];

      if (token) {
        Axios.defaults.headers.common["Authorization"] = token;
      } else {
        delete Axios.defaults.headers.common["Authorization"];
      }
    },

    GLOBAL_LOADING_START(state) {
      state.loading.globalCount++;
    },
    GLOBAL_LOADING_STOP(state) {
      state.loading.globalCount = Math.max(0, state.loading.globalCount - 1);
    },
    USER_LOADING(state, isLoading) {
      state.loading.userRequest = !!isLoading;
    },
  },

  actions: {
    AUTH_REQUEST: async ({ commit, dispatch }, user) => {
      const ipsOrigem = await getPublicIpsForLogin();
      return new Promise((resolve, reject) => {
        commit("RESET_DASHBOARD_CACHE");
        commit("AUTH_REQUEST");

        const finishLogin = (resp) => {
          const token = resp.headers.authorization;
          commit("SET_TOKEN", token);
          commit("AUTH_SUCCESS", token);

          dispatch("USER_REQUEST")
            .then(() => {
              resolve(resp);

              Promise.resolve()
                .then(() =>
                  dispatch("ENSURE_PUSH_TOKEN_SYNC", {
                    requestPermission: false,
                  })
                )
                .catch((e) => {
                  console.warn("ENSURE_PUSH_TOKEN_SYNC error after login", e);
                });
            })
            .catch((e) => {
              console.error("USER_REQUEST error", e);
              commit("REMOVE_TOKEN");
              const profileError = new Error("USER_REQUEST_FAILED");
              profileError.profileLoadFailed = true;
              reject(profileError);
            });
        };

        const rejectTwoFactor = (resp) => {
          commit("AUTH_TWO_FACTOR_REQUIRED");
          localStorage.removeItem("user-token");
          delete Axios.defaults.headers.common["Authorization"];
          const twoFactorError = new Error("TWO_FACTOR_REQUIRED");
          twoFactorError.twoFactorRequired = true;
          twoFactorError.response = resp;
          reject(twoFactorError);
        };

        const payload = withTwoFactorDevice({
          ...(user || {}),
          origemLogin: "CAJUPE_WEB",
          ...ipsOrigem,
        });
        postLogin(payload)
          .then((resp) => {
            if (resp.status === 202 && resp.data?.autenticacaoDoisFatores) {
              if (resp.data?.permitePasskey && resp.data?.passkeyAutenticacao) {
                authenticateWithPasskey(resp.data.passkeyAutenticacao)
                  .then((passkeyAutenticacaoDoisFatores) => {
                    if (!passkeyAutenticacaoDoisFatores) {
                      rejectTwoFactor(resp);
                      return;
                    }
                    return Axios({
                      url: "/login",
                      data: withTwoFactorDevice({
                        ...payload,
                        passkeyAutenticacaoDoisFatores,
                      }),
                      method: "POST",
                    })
                      .then((passkeyResp) => {
                        if (
                          passkeyResp.status === 202 &&
                          passkeyResp.data?.autenticacaoDoisFatores
                        ) {
                          rejectTwoFactor(resp);
                          return;
                        }
                        finishLogin(passkeyResp);
                      })
                      .catch(() => rejectTwoFactor(resp));
                  })
                  .catch(() => rejectTwoFactor(resp));
                return;
              }
              rejectTwoFactor(resp);
              return;
            }

            finishLogin(resp);
          })
          .catch((err) => {
            commit("AUTH_ERROR", err);
            localStorage.removeItem("user-token");
            delete Axios.defaults.headers.common["Authorization"];
            reject(err);
          });
      });
    },

    AUTH_LOGOUT: ({ commit }) => {
      return new Promise((resolve) => {
        commit("REMOVE_TOKEN");
        resolve();
      });
    },

    USER_REQUEST: async ({ commit }, options = {}) => {
      const expectedTenantId = normalizeIdentity(options?.expectedTenantId);
      const maxAttempts = expectedTenantId
        ? Math.max(Number(options?.attempts) || 1, 1)
        : 1;
      const delayMs = Math.max(Number(options?.delayMs) || 250, 0);

      commit("GLOBAL_LOADING_START");
      commit("USER_LOADING", true);

      try {
        let lastTenantId = "";

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
          const userResp = await UsuarioDataService.getUserSimplified();
          lastTenantId = normalizeIdentity(userResp?.data?.inquilino?.id);

          if (!expectedTenantId || lastTenantId === expectedTenantId) {
            commit("SET_USER", userResp);
            return userResp;
          }

          if (attempt < maxAttempts && delayMs > 0) {
            await wait(delayMs);
          }
        }

        throw new Error(
          `USER_REQUEST tenant mismatch: expected ${expectedTenantId}, received ${lastTenantId}`
        );
      } catch (e) {
        console.error("USER_REQUEST failure", e);
        throw e;
      } finally {
        commit("USER_LOADING", false);
        commit("GLOBAL_LOADING_STOP");
      }
    },

    HYDRATE_FROM_LOCAL({ commit }) {
      commit("HYDRATE_FROM_LOCAL");
      return Promise.resolve();
    },

    SET_SKIP_OPTION_MODAL: ({ commit }, payload) => {
      commit("SET_SKIP_OPTION_MODAL", payload);
    },

    SET_PENDING_TUTORIAL: ({ commit }, payload) => {
      commit("SET_PENDING_TUTORIAL", payload);
    },

    // === Sincroniza token FCM Web com o backend ===
    // forcar: registra o token deste navegador mesmo que pareça igual ao do servidor
    // (outro navegador pode ter se registrado depois), para os pushes virem para cá.
    ENSURE_PUSH_TOKEN_SYNC: async (
      { state },
      { requestPermission = false, forcar = false } = {}
    ) => {
      try {
        if (!state?.token) return;

        const browserToken = await getBrowserFcmToken({ requestPermission });
        console.log(
          "[FCM] browserToken =",
          browserToken ? browserToken.slice(0, 12) + "..." : null
        );

        if (!browserToken) return;

        const local = localStorage.getItem("fcm_web_token") || null;
        const server = state?.user?.pessoa?.pushTokenWeb || null;

        if (forcar || browserToken !== local || browserToken !== server) {
          try {
            await UsuarioDataService.changeMyTokenWeb({ token: browserToken });
            localStorage.setItem("fcm_web_token", browserToken);
            console.log("[FCM] token sincronizado com servidor");
          } catch (e) {
            console.warn("[FCM] falha ao sincronizar token no servidor:", e);
          }
        }
      } catch (e) {
        console.error("ENSURE_PUSH_TOKEN_SYNC error:", e);
      }
    },

    // === Dispara ações por função recebida via push ===
    HANDLE_PUSH_FUNCTION: async (
      { commit, dispatch, state },
      { functionName, payload, rawData, message }
    ) => {
      const fn = String(functionName || "").toUpperCase();

      let parsedPayload = parseMaybeJson(payload);
      if (
        !parsedPayload &&
        rawData &&
        Object.prototype.hasOwnProperty.call(rawData, "payload")
      ) {
        const alt = parseMaybeJson(rawData.payload);
        parsedPayload = alt !== null ? alt : rawData.payload;
      }

      const detail = {
        functionName: fn,
        payload: parsedPayload ?? payload ?? null,
        rawData: rawData || null,
        message: message || null,
      };

      try {
        console.log("[FCM] HANDLE_PUSH_FUNCTION:", fn, detail.payload);
        switch (fn) {
          case "ATUALIZADADOSUSUARIO":
          case "ATUALIZARDADOSUSUARIO":
          case "REQUESTUSERINFO": {
            // Aviso da troca de empresa feita por esta aba, que já recarregou os dados.
            if (ehPushDaPropriaTroca(detail.payload)) break;
            const previousTenantIdentity = getTenantIdentity(state);
            await dispatch("USER_REQUEST");
            if (previousTenantIdentity !== getTenantIdentity(state)) {
              commit("RESET_DASHBOARD_CACHE");
            }
            break;
          }
          case "BUSCANOTIFICACAOSERVIDOR":
          case "SEARCHSERVERNOTIFICATIONS":
            if (typeof window !== "undefined") {
              window.dispatchEvent(new CustomEvent("support:push", { detail }));
            }
            break;
          default:
            break;
        }
      } catch (e) {
        console.error("[FCM] HANDLE_PUSH_FUNCTION error:", e);
      } finally {
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("push:function", { detail }));
        }
      }
    },
  },

  getters: {
    isAuthenticated: (state) => !!state.token && state.token.length > 10,
    authStatus: (state) => state.status,
    quantidadePermissoes: (state) => (state.permissoes || []).length,
    locale: () => "pt-br",
    isUserLoading: (state) => !!state.loading.userRequest,
    isGlobalLoading: (state) => state.loading.globalCount > 0,
    isPasswordChangeRequired: (state) =>
      state.user?.alteracaoSenhaObrigatoria === true,
    limiteSecuritarioRoteiro: (state) => (tipoAtendimentoRoteiro) => {
      const tipo = String(tipoAtendimentoRoteiro || "URBANO").toUpperCase();
      return tipo === "INTERURBANO"
        ? state.limitesSecuritariosRoteiro.INTERURBANO
        : state.limitesSecuritariosRoteiro.URBANO;
    },
  },
});
